/** Calibrated Indian highway freight circuity factor. */
export const HIGHWAY_CIRCUITY_FACTOR = 1.22;

/** Average commercial truck speed in km/h. */
export const COMMERCIAL_TRUCK_SPEED_KMH = 55.0;

/** Formats road distance in km (e.g. "624 km"). */
export function formatDistance(distanceKm?: number | null): string | null {
  if (distanceKm == null || distanceKm <= 0) return null;
  return `${Math.round(distanceKm)} km`;
}

/** Formats transit duration in hours and minutes (e.g. "~11h 30m"). */
export function formatDuration(minutes?: number | null): string | null {
  if (minutes == null || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0 && remainingMinutes > 0) {
    return `~${hours}h ${remainingMinutes}m`;
  }
  if (hours > 0) {
    return `~${hours}h`;
  }
  return `~${remainingMinutes}m`;
}

/** Formats compact duration (e.g. "~11.5 hrs", "~1 hr", "~45m"). */
export function formatCompactDuration(minutes?: number | null): string | null {
  if (minutes == null || minutes <= 0) return null;
  if (minutes < 60) {
    return `~${minutes}m`;
  }
  const hours = minutes / 60.0;
  if (hours === 1.0) {
    return '~1 hr';
  }
  if (hours % 1 === 0) {
    return `~${hours.toFixed(0)} hrs`;
  }
  return `~${hours.toFixed(1)} hrs`;
}

/** Computes straight-line Haversine distance * 1.22 circuity factor. */
export function calculateHaversineDistanceKm(
  lat1?: number | null,
  lng1?: number | null,
  lat2?: number | null,
  lng2?: number | null,
): number | null {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) {
    return null;
  }
  if ((lat1 === 0 && lng1 === 0) || (lat2 === 0 && lng2 === 0)) {
    return null;
  }
  if (lat1 === lat2 && lng1 === lng2) {
    return 0;
  }

  const r = 6371.0; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180.0;
  const dLng = ((lng2 - lng1) * Math.PI) / 180.0;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180.0) *
      Math.cos((lat2 * Math.PI) / 180.0) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const straightLineKm = r * c;

  return Math.round(straightLineKm * HIGHWAY_CIRCUITY_FACTOR * 100) / 100;
}
